import '@data/Simulations/pipeline/blocks/SliceBlock';
import type { SliceParams } from '@data/Simulations/pipeline/blocks/SliceBlock';
import { ControllerManager } from '@gui/controller/ControllerManager';
import RangeSlider from '@gui/components/generic/RangeSider';
import { Box } from '@mui/material';
import type { BlockParameters } from '@visualizer/types';
import Grid from 'Geodata/grid';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { usePipelineBlockParams } from './usePipelineBlockParams';

type SliceParametersProps = {
    blockId: string;
};

const createDefaultSliceRange = (max?: number): [number, number] => [0, max ?? 0];

export const getSliceDefaultParams = (): Pick<SliceParams, 'slicex' | 'slicey' | 'slicez'> => ({
    slicex: createDefaultSliceRange(Grid.gridX),
    slicey: createDefaultSliceRange(Grid.gridY),

    /* DO NOT TOUCH! MUST STAY GRID.MAXFLOOR  */
    slicez: createDefaultSliceRange(Grid.maxFloor),
});

const SliceParameters: FC<SliceParametersProps> = ({ blockId }) => {
    const params = usePipelineBlockParams<SliceParams>(blockId);
    const pipelineController = useMemo(() => ControllerManager.getInstance().getPipelineController(), []);

    const updateParams = (partial: Partial<SliceParams>) => {
        pipelineController.updateBlockParams(blockId, partial as BlockParameters);
    };

    const defaultRanges = getSliceDefaultParams();
    const defaultSliceX = defaultRanges.slicex;
    const defaultSliceY = defaultRanges.slicey;
    const defaultSliceZ = defaultRanges.slicez;

    useEffect(() => {
        if (!params) {
            updateParams({
                slicex: defaultSliceX,
                slicey: defaultSliceY,
                slicez: defaultSliceZ,
            });
            return;
        }

        const missing: Partial<SliceParams> = {};
        if (!params.slicex) {
            missing.slicex = defaultSliceX;
        }
        if (!params.slicey) {
            missing.slicey = defaultSliceY;
        }
        if (!params.slicez) {
            missing.slicez = defaultSliceZ;
        }

        if (Object.keys(missing).length > 0) {
            updateParams(missing);
        }
    }, [params]);

    const slicex = params?.slicex ?? defaultSliceX;
    const slicey = params?.slicey ?? defaultSliceY;
    const slicez = params?.slicez ?? defaultSliceZ;

    const handleSliceChange = (key: 'slicex' | 'slicey' | 'slicez') => (next: [number, number]) => {
        updateParams({
            [key]: next,
        });
    };

    return (
        <Box sx={{ pr: 1.2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <RangeSlider
                    value={slicex}
                    lastIndex={Grid.gridX}
                    showValues
                    size="small"
                    step={1}
                    onChange={handleSliceChange('slicex')}
                    sx={{ ml: '5px', mr: '10px', flex: 1, minWidth: 0 }}
                />
                <Box>X</Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <RangeSlider
                    value={slicey}
                    lastIndex={Grid.gridY}
                    showValues
                    size="small"
                    step={1}
                    onChange={handleSliceChange('slicey')}
                    sx={{ ml: '5px', mr: '10px', flex: 1, minWidth: 0 }}
                />
                <Box>Y</Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <RangeSlider
                    value={slicez}
                    lastIndex={Grid.maxFloor}
                    showValues
                    size="small"
                    step={1}
                    onChange={handleSliceChange('slicez')}
                    sx={{ ml: '5px', mr: '10px', flex: 1, minWidth: 0 }}
                />
                <Box>Z</Box>
            </Box>
        </Box>
    );
};

export default SliceParameters;
